import React, { useState } from 'react';
import { Button, Input, Modal, Select, Table, Tag } from 'antd';
import { DeleteOutlined, PlusOutlined, EditOutlined } from '@ant-design/icons';
import { LocationItem } from './ItemsEditor';

type Props = {
  value: LocationItem[];
  onChange: (items: LocationItem[]) => void;
  disabled?: boolean;
};

const ItemsEditorModal = ({ value, onChange, disabled = false }: Props): JSX.Element => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<LocationItem[]>(value ?? []);

  const handleOpen = () => {
    setItems(value ?? []);
    setOpen(true);
  };

  const handleOk = () => {
    onChange(items);
    setOpen(false);
  };

  const handleCancel = () => {
    setItems(value ?? []);
    setOpen(false);
  };

  const addItem = () => {
    setItems((prev) => [...prev, { item_name: '', capacity: 0, operation: 'pickup' }]);
  };

  const removeItem = (index: number) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const updateItem = (index: number, field: keyof LocationItem, val: any) => {
    setItems((prev) => prev.map((item, i) => (i === index ? { ...item, [field]: val } : item)));
  };

  const columns = [
    {
      title: 'Item Name',
      key: 'item_name',
      render: (_: any, record: LocationItem, index: number) => (
        <Input
          value={record.item_name}
          placeholder="Item name"
          onChange={(e) => updateItem(index, 'item_name', e.target.value)}
        />
      ),
    },
    {
      title: 'Capacity',
      key: 'capacity',
      width: 100,
      render: (_: any, record: LocationItem, index: number) => (
        <Input
          type="number"
          value={record.capacity}
          min={0}
          onChange={(e) => updateItem(index, 'capacity', Number(e.target.value))}
        />
      ),
    },
    {
      title: 'Operation',
      key: 'operation',
      width: 130,
      render: (_: any, record: LocationItem, index: number) => (
        <Select
          value={record.operation}
          onChange={(val) => updateItem(index, 'operation', val)}
          style={{ width: '100%' }}
        >
          <Select.Option value="pickup">Pickup</Select.Option>
          <Select.Option value="drop_off">Drop Off</Select.Option>
        </Select>
      ),
    },
    {
      title: '',
      key: 'action',
      width: 50,
      render: (_: any, __: any, index: number) => (
        <Button icon={<DeleteOutlined />} danger size="small" onClick={() => removeItem(index)} />
      ),
    },
  ];

  const preview = value ?? [];

  return (
    <>
      <Button icon={<EditOutlined />} size="small" onClick={handleOpen} disabled={disabled}>
        {preview.length > 0 ? `${preview.length} item(s)` : 'Add Items'}
      </Button>

      <Modal
        title="Edit Items"
        visible={open}
        onOk={handleOk}
        onCancel={handleCancel}
        width={600}
        okText="Save"
        destroyOnClose
      >
        <Table
          columns={columns}
          dataSource={items.map((item, i) => ({ ...item, key: i }))}
          pagination={false}
          size="small"
          footer={() => (
            <Button icon={<PlusOutlined />} onClick={addItem} type="dashed" block>
              Add Item
            </Button>
          )}
        />
        {items.length > 0 && (
          <div className="mg-t-12">
            {['pickup', 'drop_off'].map((op) => {
              const total = items
                .filter((i) => i.operation === op)
                .reduce((sum, i) => sum + Number(i.capacity ?? 0), 0);
              if (total === 0) {
                return null;
              }
              return (
                <Tag key={op} color={op === 'pickup' ? 'green' : 'red'} style={{ marginRight: 8 }}>
                  {op === 'pickup' ? '↑ Pickup' : '↓ Drop Off'}: {total}
                </Tag>
              );
            })}
          </div>
        )}
      </Modal>
    </>
  );
};

ItemsEditorModal.defaultProps = {
  disabled: false,
};

export default ItemsEditorModal;
